import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import ModalContainer from '@components/modals/ModalContainer';
import CommonImage from '@components/CommonImage';
import ModalHeader from '@components/modals/ModalHeader';

import AddReview from '@features/supplier/component/AddReview';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import { getExFactorySupplierQuote, updateExFactorySupplierQuote } from '@services/quoteServices';

import { getUserInfo, isAdmin, renderMultiColor, validateFloatNumber, validateNumber } from '@utils/Util';

const ExFactorySupplierQuote = ({ onCloseModal, rfqId, onSuccess, supplierDetails }) => {
	const [isShowReviewModal, setIsShowReviewModal] = useState(false);
	const [quotes, setQuotes] = useState({});
	const [defaultQuotes, setDefaultQuotes] = useState({});
	const [errors, setErrors] = useState({});
	const [buyerPriceUpdated, setBuyerPriceUpdated] = useState(false);
	const [factoryPriceUpdated, setFactoryPriceUpdated] = useState(false);
	const [isShowConfirmation, setIsShowConfirmation] = useState(false);
	const [unitPriceWiseQuantity, setUnitPriceWiseQuantity] = useState({});
	const dispatch = useDispatch();

	useEffect(() => {
		(async () => {
			const data = await getExFactorySupplierQuote(rfqId);
			let priceWiseQuantity = data?.unitPriceWiseQuantity
				? Object.fromEntries(
						Object.entries(data.unitPriceWiseQuantity).map(([k, v]) => [k, v === -1 ? '' : v])
					)
				: {};
			setUnitPriceWiseQuantity(priceWiseQuantity);
			data.exFactoryQuantity = data?.exFactoryQuantity || calculateExFactoryQty(priceWiseQuantity);
			data.exFactoryAmount = Object.entries(priceWiseQuantity).reduce(
				(sum, [key, value]) => sum + Number(key) * Number(value),
				0
			);
			setDefaultQuotes(data);
			setQuotes(data);
		})();
	}, [rfqId]);

	const renderSizeWiseTableHeading = () => {
		return (
			<thead>
				<tr>
					{quotes?.colorWiseSizeQuantityPairList?.length > 0 &&
						quotes.colorWiseSizeQuantityPairList[0].sizeQuantityPairList.map((value) => (
							<th key={value.id}>
								<p className="semibold-14 col-heading">{value.label}</p>
							</th>
						))}
				</tr>
			</thead>
		);
	};

	const renderQuoteTypes = (type) => {
		const labelType = type === 'BUYER' ? 'Buyer' : 'Factory';
		const quotationType = type === 'BUYER' ? quotes?.buyerQuotationType : quotes?.factoryQuotationType;
		const priceType = quotes?.factoryPriceType;
		return (
			<div className="d-flex justify-content-between align-self-center my-3">
				<p className="regular-14 gray_dark_02">{labelType} price</p>
				<div className="d-flex justify-content-between align-self-center gap-3">
					{quotationType && (
						<p className="gray_dark_02 regular-14">
							{quotationType} <CommonImage src="/icons/down-arrow.png" />
						</p>
					)}
					{priceType && (
						<p className="gray_dark_02 regular-14 ml-2">
							{priceType} <CommonImage src="/icons/down-arrow.png" />
						</p>
					)}
				</div>
			</div>
		);
	};

	const getSizeWisePrice = (sizeWisePrices, id) => {
		if (sizeWisePrices[id]) {
			return sizeWisePrices[id];
		}
		return null;
	};

	const getColorWisePrice = (colorWisePrices, id) => {
		if (colorWisePrices[id]) {
			return colorWisePrices[id];
		}
		return null;
	};

	const renderFactoryWiseTableHeading = () => {
		if (quotes?.factoryQuotationType === 'COLORWISE') {
			return (
				<thead>
					<tr>
						{quotes?.colorWiseSizeQuantityPairList &&
							quotes.colorWiseSizeQuantityPairList.map((value) => (
								<th key={value.id}>
									<span>{renderMultiColor(value)}</span>
								</th>
							))}
					</tr>
				</thead>
			);
		} else if (quotes?.factoryQuotationType === 'SIZEWISE') {
			return renderSizeWiseTableHeading();
		}
	};

	const onChangePriceSizeOrColorWise = (quoteType, priceType, e, sizeId) => {
		const { value } = e.target;
		let fieldName = '';
		if (quoteType === 'BUYER' && priceType === 'COLORWISE') {
			fieldName = 'colorWiseBuyerPrice';
		}
		if (quoteType === 'BUYER' && priceType === 'SIZEWISE') {
			fieldName = 'sizeWiseBuyerPrice';
		}
		if (quoteType !== 'BUYER' && priceType === 'COLORWISE') {
			fieldName = 'colorWiseFactoryPrice';
		}
		if (quoteType !== 'BUYER' && priceType === 'SIZEWISE') {
			fieldName = 'sizeWiseFactoryPrice';
		}
		let modifiedPrice = { ...quotes[fieldName] };
		modifiedPrice[sizeId] = value;

		setQuotes((prev) => ({ ...prev, [fieldName]: modifiedPrice }));
		if (quoteType === 'BUYER') {
			setBuyerPriceUpdated(true);
		} else {
			setFactoryPriceUpdated(true);
		}
	};

	const onChangeQty = () => {
		let priceObj =
			quotes?.buyerQuotationType === 'SIZEWISE'
				? quotes?.sizeWiseBuyerPrice
				: quotes?.buyerQuotationType === 'COLORWISE'
					? quotes?.colorWiseBuyerPrice
					: { designWiseBuyerPrice: quotes.designWiseBuyerPrice };

		let tmpPriceObj = {};
		Object.values(priceObj).map((item) => {
			tmpPriceObj[item] = unitPriceWiseQuantity[item] || '';
		});
		setUnitPriceWiseQuantity(tmpPriceObj);
		let factoryQty = calculateExFactoryQty(tmpPriceObj);
		if (factoryQty) {
			let updatedObj = { exFactoryQuantity: factoryQty };
			updatedObj.exFactoryAmount = Object.entries(tmpPriceObj).reduce(
				(sum, [key, value]) => sum + Number(key) * Number(value),
				0
			);
			setQuotes((prev) => ({
				...prev,
				...updatedObj
			}));
		}
	};

	const renderTableBody = (type) => {
		let quotationType = type === 'BUYER' ? quotes?.buyerQuotationType : quotes?.factoryQuotationType;
		let colorWisePrice = type === 'BUYER' ? quotes?.colorWiseBuyerPrice : quotes?.colorWiseFactoryPrice;
		let sizeWisePrice = type === 'BUYER' ? quotes?.sizeWiseBuyerPrice : quotes?.sizeWiseFactoryPrice;
		if (quotationType === 'COLORWISE') {
			return (
				<tbody>
					<tr>
						{quotes?.colorWiseSizeQuantityPairList &&
							quotes.colorWiseSizeQuantityPairList.map((value) => (
								<td key={value.id}>
									<input
										type="number"
										min={1}
										value={getColorWisePrice(colorWisePrice, value.id)}
										placeholder="$00.00"
										onChange={(e) =>
											onChangePriceSizeOrColorWise(type, quotationType, e, value.id)
										}
										onBlur={onChangeQty}
										onKeyPress={validateFloatNumber}
										readOnly={isReadOnly()}
									/>
								</td>
							))}
					</tr>
				</tbody>
			);
		} else if (quotationType === 'SIZEWISE') {
			return (
				<tbody>
					<tr>
						{quotes?.colorWiseSizeQuantityPairList.length > 0 &&
							quotes.colorWiseSizeQuantityPairList[0].sizeQuantityPairList.map((value) => (
								<td key={value.id}>
									<input
										type="number"
										min={1}
										placeholder="$00.00"
										value={getSizeWisePrice(sizeWisePrice, value.id)}
										onChange={(e) =>
											onChangePriceSizeOrColorWise(type, quotationType, e, value.id)
										}
										onBlur={onChangeQty}
										onKeyPress={validateFloatNumber}
										readOnly={isReadOnly()}
									/>
								</td>
							))}
					</tr>
				</tbody>
			);
		}
	};

	const isReadOnly = () => {
		return (
			defaultQuotes?.exFactoryDate &&
			defaultQuotes?.exFactoryQuantity &&
			getUserInfo().primaryUserType !== 'ADMIN'
		);
	};

	const handleChange = (e) => {
		const { name, value } = e.target;
		setQuotes((prev) => ({ ...prev, [name]: value }));
		if (name === 'designWiseBuyerPrice') {
			setBuyerPriceUpdated(true);
		}
		if (name === 'designWiseFactoryPrice') {
			setFactoryPriceUpdated(true);
		}
	};

	const calculateExFactoryQty = (unitPriceWiseQuantity) => {
		let sum = 0;
		for (let item in unitPriceWiseQuantity) {
			if (unitPriceWiseQuantity[item] !== '') {
				sum += parseInt(unitPriceWiseQuantity[item]);
			}
		}
		return sum;
	};

	const isInvalid = () => {
		let errorObj = {};
		const { buyerQuotationType, colorWiseBuyerPrice, sizeWiseBuyerPrice } = quotes;
		let priceObj =
			buyerQuotationType === 'SIZEWISE'
				? sizeWiseBuyerPrice
				: buyerQuotationType === 'COLORWISE'
					? colorWiseBuyerPrice
					: { designWise: quotes.designWiseBuyerPrice };

		if (!quotes?.exFactoryDate) {
			errorObj['exFactoryDate'] = 'Delivery date is required!';
		}
		if (
			Object.values(unitPriceWiseQuantity).length !==
			[...new Set(Object.values(priceObj).map(String))].length
		) {
			errorObj['exFactoryQuantity'] = 'Quantity is required!';
		}
		if (!quotes?.exFactoryAmount) {
			errorObj['exFactoryAmount'] = 'Factory value is required!';
		}
		if (quotes?.exFactoryAmount && quotes?.exFactoryAmount > quotes?.amount * 1.3) {
			const percentageAbove = (quotes.exFactoryAmount / quotes.amount - 1.3) * 100;
			errorObj['exFactoryAmount'] =
				`Your extra shipment value is ${percentageAbove.toFixed(2)}% please recheck`;
		}
		if (quotes?.buyerQuotationType === 'DESIGNWISE' && !quotes?.designWiseBuyerPrice) {
			errorObj['designWiseBuyerPrice'] = 'Price is required!';
		}
		if (quotes?.factoryQuotationType === 'DESIGNWISE' && !quotes?.designWiseFactoryPrice) {
			errorObj['designWiseFactoryPrice'] = 'Price is required!';
		}
		setErrors(errorObj);
		return Object.keys(errorObj).length > 0;
	};

	const handleSubmit = async () => {
		if (!isInvalid()) {
			try {
				dispatch(openLoader());
				await updateExFactorySupplierQuote(rfqId, {
					...quotes,
					buyerPriceUpdated,
					factoryPriceUpdated,
					unitPriceWiseExFactoryQuantity: unitPriceWiseQuantity,
					exFactoryQuantity: calculateExFactoryQty(unitPriceWiseQuantity)
				});
				if (quotes?.needFactoryFeedback === true) {
					setIsShowReviewModal(true);
				} else {
					onSuccess();
				}
				dispatch(closeLoader());
			} catch {
				dispatch(closeLoader());
			}
		}
	};

	const renderSippedQuantity = () => {
		const handleChangeSippedQuantity = (e) => {
			const { name, value } = e.target;
			let tmpPriceObj = { ...unitPriceWiseQuantity, [name]: value };
			setUnitPriceWiseQuantity(tmpPriceObj);
			let exFactoryAmount = Object.entries(tmpPriceObj).reduce(
				(sum, [key, value]) => sum + Number(key) * Number(value),
				0
			);
			let factoryQty = calculateExFactoryQty(tmpPriceObj);
			setQuotes((prev) => ({
				...prev,
				exFactoryAmount,
				exFactoryQuantity: factoryQty
			}));
		};

		if (unitPriceWiseQuantity) {
			return Object.entries(unitPriceWiseQuantity).map(([key, value]) => (
				<div className="col-md-3" key={`input_${key}`}>
					<div className="from-group mt-2">
						<label>Qty for ${key}</label>
						<input
							type="text"
							name={key}
							value={value || ''}
							onChange={handleChangeSippedQuantity}
							onKeyPress={validateNumber}
							readOnly={isReadOnly()}
						/>
						{errors?.exFactoryQuantity && (
							<p className="error d-block">{errors.exFactoryQuantity}</p>
						)}
					</div>
				</div>
			));
		}
	};

	return (
		<>
			{!isShowReviewModal && (
				<ModalContainer onCloseModal={onCloseModal} className="color-and-size-order-modal">
					<div className="popup-wrapper width-720-modal">
						<div className="common-popup color-and-size-order-modal">
							<ModalHeader title="Shipment Details" onClose={onCloseModal} />
							<div className="common-popup-body scroll-y-label">
								<div className="delivery-and-qunt-header">
									<div className="container px-0">
										<div className="row">
											<div className="col-md-6">
												<div className="from-group">
													<label>Ex-factory date</label>
													<input
														type="date"
														name="exFactoryDate"
														value={quotes?.exFactoryDate || ''}
														onChange={handleChange}
														min={
															isAdmin()
																? undefined
																: new Date(
																		new Date().setDate(
																			new Date().getDate() - 4
																		)
																	)
																		.toISOString()
																		.split('T')[0]
														}
														readOnly={isReadOnly()}
													/>
													{errors?.exFactoryDate && (
														<p className="error d-block">
															{errors.exFactoryDate}
														</p>
													)}
												</div>
											</div>
											<div className="col-md-6">
												<div className="from-group">
													<label>Ex-factory value</label>
													<input
														type="text"
														name="exFactoryAmount"
														value={quotes?.exFactoryAmount?.toFixed(2) || ''}
														onChange={handleChange}
														onKeyPress={validateNumber}
														readOnly={true}
													/>
													{errors?.exFactoryAmount && (
														<p className="error d-block">
															{errors.exFactoryAmount}
														</p>
													)}
												</div>
											</div>
										</div>
										<div className="row">{renderSippedQuantity()}</div>
									</div>
								</div>
								<div className="mt-4">
									<div className="create-order-container p-0">
										<div className="design-quantity-table">
											{renderQuoteTypes('BUYER')}
											<div className="design-table d-block">
												<div className="category-wise-table sizewise-wise-table">
													{quotes?.buyerQuotationType !== 'DESIGNWISE' && (
														<table className="table">
															{renderSizeWiseTableHeading()}
															{renderTableBody('BUYER')}
														</table>
													)}
													{quotes?.buyerQuotationType === 'DESIGNWISE' && (
														<div className="d-flex justify-content-end">
															<div className="from-group">
																<input
																	type="text"
																	value={quotes?.designWiseBuyerPrice || ''}
																	name="designWiseBuyerPrice"
																	onChange={handleChange}
																	onBlur={onChangeQty}
																	onKeyPress={validateFloatNumber}
																	readOnly={isReadOnly()}
																/>
																{errors?.designWiseBuyerPrice && (
																	<p className="error">
																		{errors.designWiseBuyerPrice}
																	</p>
																)}
															</div>
														</div>
													)}
												</div>
											</div>
										</div>

										<div className="design-quantity-table">
											{renderQuoteTypes('FACTORY')}
											<div className="design-table d-block">
												<div className="category-wise-table sizewise-wise-table">
													{quotes?.factoryQuotationType !== 'DESIGNWISE' && (
														<table className="table">
															{renderFactoryWiseTableHeading()}
															{renderTableBody('FACTORY')}
														</table>
													)}
													{quotes?.factoryQuotationType === 'DESIGNWISE' && (
														<div className="d-flex justify-content-end align-self-center">
															<div className="from-group">
																<input
																	type="text"
																	value={
																		quotes?.designWiseFactoryPrice || ''
																	}
																	name="designWiseFactoryPrice"
																	onChange={handleChange}
																	onKeyPress={validateFloatNumber}
																	readOnly={isReadOnly()}
																/>
																{errors?.designWiseFactoryPrice && (
																	<p className="error">
																		{errors.designWiseFactoryPrice}
																	</p>
																)}
															</div>
														</div>
													)}
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
							<div className="modal-footer">
								<div className="footer-buttons text-right">
									{isShowConfirmation && (
										<span className="text-warning regular-14">
											You will not be able to change ex factory data later.
										</span>
									)}
									<button className="button outline size32 ml-2" onClick={onCloseModal}>
										Cancel
									</button>
									{isShowConfirmation && (
										<button className="button size32 ml-2" onClick={handleSubmit}>
											Confirm
										</button>
									)}
									{!isShowConfirmation && (
										<button
											className="button size32 ml-2"
											onClick={() => setIsShowConfirmation(true)}
										>
											Submit
										</button>
									)}
								</div>
							</div>
						</div>
					</div>
				</ModalContainer>
			)}
			{isShowReviewModal && (
				<AddReview
					onSuccess={onSuccess}
					onCloseReview={onCloseModal}
					supplierDetails={supplierDetails}
				/>
			)}
		</>
	);
};

export default ExFactorySupplierQuote;
